import React, { useEffect, useRef, useState } from "react";
import { KitClient, KitDetailModel } from "@app/api2/api";
import { Container, Grid, GridColumn, GridRow } from "@/components/bp/bp-ui";
import { InfoBox } from "@common/InfoBox";
import { PageHeader } from "@common/PageHeader";
import { BPSection } from "@common/BPSection";
import { BPReactTable } from "@common/BPReactTable";
import EditableText from "@common/EditableText";
import { useUser } from "../Contexts/UserContext";
import { kitClient } from "@app/api2/Clients";
import { BPContainer } from "@/components/bp/bpcontainer";

export interface IKitDetailsProps {
    kitId: number;
}

export const KitsDetails: React.FC<IKitDetailsProps> = ({ kitId }) => {
    const clientRef = useRef<KitClient>(kitClient);
    const [model, setModel] = useState<KitDetailModel>({ systems: [] } as KitDetailModel);
    const user = useUser();

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const m = await clientRef.current.get(kitId);
                if (mounted) {
                    setModel(m);
                }
            } catch (err) {
                console.error("Failed to load kit details", err);
            }
        })();
        return () => { mounted = false; };
    }, [kitId]);

    const updateFullName = async (newFullName: string) => {
        try {
            // Call API (updateKit signature: updateKit(id, fullName))
            await clientRef.current.updateKit(kitId, { fullName: newFullName });
            setModel(prev => ({
                ...prev,
                fullName: newFullName
            }));
        } catch (err) {
            console.error("Failed to update kit full name", err);
            // Rethrow so EditableText can show inline error (it expects onSave may reject)
            throw err;
        }
    };

    return (
        <BPContainer>
            <PageHeader header={window.translation.kit.kitDetails} />
            <Grid>
                <GridRow>
                    <GridColumn>
                        <InfoBox header={window.translation.common.information} items={[
                            { label: window.translation.kit.name, description: model.shortName },
                            {
                                label: window.translation.kit.fullName,
                                description: (
                                    <EditableText
                                        value={model.fullName || ""}
                                        placeholder={window.translation.kit.fullName}
                                        onSave={updateFullName}
                                        canEdit={user?.user?.isSiteAdmin ?? false}
                                    />
                                )
                            }
                        ]} />
                    </GridColumn>
                </GridRow>
                <GridRow>
                    <GridColumn>
                        <BPSection header={window.translation.kit.markers}>
                            <BPReactTable
                                disablePagination
                                disableGlobalFilter
                                disableSortBy
                                stickyHeader
                                celled
                                data={model.systems}
                                renderRowSubComponent={row =>
                                    <BPReactTable
                                        disablePagination
                                        disableGlobalFilter
                                        disableSortBy
                                        stickyHeader
                                        celled
                                        data={row.alleles}
                                        columns={[
                                            { accessor: 'allele', Header: window.translation.kit.allele },
                                            { accessor: 'size', Header: window.translation.kit.size },
                                            { accessor: 'sizeMin', Header: window.translation.kit.sizeMin },
                                            { accessor: 'sizeMax', Header: window.translation.kit.sizeMax },
                                            { accessor: 'virtual', Header: window.translation.kit.virtual },
                                            { accessor: 'active', Header: window.translation.common.active },
                                        ]}
                                    />
                                }
                                columns={[
                                    { accessor: 'name', Header: window.translation.kit.marker },
                                    { accessor: 'color', Header: window.translation.kit.color },
                                    { accessor: 'repeat', Header: window.translation.kit.repeat },
                                    { accessor: 'markerMin', Header: window.translation.kit.markerMin },
                                    { accessor: 'markerMax', Header: window.translation.kit.markerMax },
                                    { accessor: 'offset', Header: window.translation.kit.offset },
                                    { accessor: 'genderMarker', Header: window.translation.kit.genderMarker, Cell: ({ value }) => (value ? window.translation.common.yes : '') },
                                    { accessor: 'qualitySensor', Header: window.translation.kit.qualitySensor, Cell: ({ value }) => (value ? window.translation.common.yes : '') },
                                    { accessor: 'defaultFemaleMu', Header: window.translation.parameter.names.brutusFemaleMu },
                                    { accessor: 'defaultMaleMu', Header: window.translation.parameter.names.brutusMaleMu },
                                ]}
                            />
                        </BPSection>
                    </GridColumn>
                </GridRow>
            </Grid>
        </BPContainer>
    );
};

export default KitsDetails;